/**
 * PlatformLogin (iter308j) — rendered at `my.vyomos.io` and other
 * platform hosts. NEUTRAL brand identity ("Vyomos"), no academy
 * hero copy. Pure functional login for super admins + any user whose
 * tenant they aren't sure of.
 *
 * Tenant hosts (`<slug>.vyomos.io`, custom domains) still render
 * the full LandingPage — the public/index router below decides which
 * one to show based on `branding.is_platform`.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ShieldCheck, Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useTenant } from '../../context/TenantContext';
import { buildHandoffUrl, isPreviewHost, normHost } from '../../utils/crossHostAuth';

export default function PlatformLogin() {
  const { branding } = useTenant();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // iter308k — Use AuthContext.login() (which correctly stores the
      // token + sets the authStorage preference + updates in-memory
      // auth state). Previous direct-axios path called setAuth() with
      // swapped args, storing the user object as the token → every
      // subsequent API call sent `Bearer [object Object]` → 401 across
      // the entire dashboard.
      const userData = await login(email, password, true);
      toast.success('Welcome back');
      // iter312 — Tenant admins/staff/coaches/parents logging in on the
      // PLATFORM host (my.vyomos.io) must be bounced to their own
      // tenant's host so branding, CSS vars, PWA scope, and per-origin
      // localStorage all line up correctly. The handoff URL carries the
      // JWT in the URL fragment so the destination host (different
      // origin) can pick up the session instead of forcing a re-login.
      if (userData?.role && userData.role !== 'super_admin' && !isPreviewHost()) {
        try {
          const meRes = await fetch('/api/tenant/me', {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          });
          if (meRes.ok) {
            const me = await meRes.json();
            const targetHost = me.canonical_hostname || me.custom_domain || me.subdomain_host;
            if (
              targetHost &&
              normHost(targetHost) &&
              normHost(targetHost) !== normHost(window.location.host)
            ) {
              const path =
                userData.role === 'admin' ? '/admin'
                : userData.role === 'staff' ? '/staff'
                : userData.role === 'coach' ? '/coach'
                : userData.role === 'customer' ? '/book'
                : userData.role === 'player' ? '/player'
                : '/parent';
              const url = buildHandoffUrl(targetHost, path);
              window.location.replace(url);
              return;
            }
          }
        } catch (e) {
          // Network blip — fall through to same-host nav so the user
          // still lands somewhere usable.
          console.warn('[PlatformLogin] cross-host redirect lookup failed:', e?.message);
        }
      }
      // Route by role — super admin lands on /super-admin, everyone else on /admin
      if (userData?.role === 'super_admin') {
        navigate('/super-admin/tenants', { replace: true });
      } else {
        navigate('/admin', { replace: true });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-slate-50 px-4"
      data-testid="platform-login-page"
      style={{
        background: `linear-gradient(135deg, ${branding.primary_color}, ${branding.accent_color})`,
      }}
    >
      <div className="w-full max-w-md bg-white rounded-xl shadow-xl p-8">
        <div className="flex items-center gap-2 mb-6">
          <ShieldCheck
            className="w-6 h-6"
            style={{ color: branding.primary_color || '#0F172A' }}
          />
          <div>
            <h1 className="font-bold text-xl tracking-tight">
              {branding.business_name || 'Vyomos'}
            </h1>
            <p className="text-xs text-slate-500">
              {branding.business_tagline || 'The Academy Operating System'}
            </p>
          </div>
        </div>

        <h2 className="text-2xl font-semibold mb-1">Sign in</h2>
        <p className="text-sm text-slate-500 mb-6">
          Access your academy admin or the platform console.
        </p>

        <form onSubmit={onSubmit} className="space-y-3">
          <input
            type="email"
            required
            placeholder="you@youracademy.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full border border-slate-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
            data-testid="platform-login-email"
          />
          <input
            type="password"
            required
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full border border-slate-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
            data-testid="platform-login-password"
          />
          <button
            type="submit"
            disabled={submitting}
            data-testid="platform-login-submit"
            className="w-full text-white rounded-lg px-4 py-3 text-sm font-medium hover:opacity-95 disabled:opacity-50 flex items-center justify-center gap-2"
            style={{ background: branding.primary_color || '#0F172A' }}
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Sign in'}
          </button>
        </form>

        <div className="mt-6 text-center text-xs text-slate-500">
          <p>
            Don&apos;t know your academy&apos;s sign-in URL?{' '}
            <a
              href={`mailto:${branding.support_email || 'hello@vyomos.io'}`}
              className="text-slate-700 hover:underline font-medium"
            >
              Email support
            </a>
          </p>
          <p className="mt-2 text-[11px] text-white/70 mix-blend-overlay">
            © {new Date().getFullYear()} Vyomos
          </p>
        </div>
      </div>
    </div>
  );
}
